import { ButtonSpinner } from "@/components/ButtonSpinner";
import { Loading } from "@/components/portal/Loading";
import { Dropdown } from "@/crud/form/Dropdown";
import { BASE_URL } from "@/dashboardQueries";
import { useDarkMode } from "@/hooks";
import axios from "axios";
import clsx from "clsx";
import { Formik } from "formik";
import { values } from "lodash";
import { CornerUpRight } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { invalidateAccreditationGroups } from "@/utils/accreditation";

interface Props {
  closeAction: () => void;
  selectedIds: Set<number>;
  type: "group" | "individual";
}

export const MoveIndividualOrGroup = ({
  closeAction,
  selectedIds,
  type,
}: Props) => {
  // Fetch groups if group_selector is present
  const {
    data: groupOptions,
    isLoading: isGroupsLoading,
    isError: isGroupsError,
  } = useQuery(["accreditationGroups"], async () => {
    const response = await axios.get(`${BASE_URL}/accreditation/groups/list`);
    return response.data;
  });
  const darkMode = useDarkMode();
  // Optional groupId for clearing the query client
  const { groupId } = useParams<{ groupId: string }>();
  const queryClient = useQueryClient();

  // Determine endpoint and payload keys based on type
  const endpoint =
    type === "group"
      ? `${BASE_URL}/accreditation/groups/bulk-action`
      : `${BASE_URL}/accreditation/individuals/bulk-action`;

  const actionName = "Move";
  const idsKey = type === "group" ? "ids" : "ids"; // Both use "ids" in your backend

  // Mutation for moving groups
  const moveMutation = useMutation(
    async ({ ids, targetGroup }: { ids: number[]; targetGroup: number }) => {
      const payload =
        type === "group"
          ? { action: actionName, ids, target_group: targetGroup }
          : { action: actionName, ids, target_group: targetGroup };
      const response = await axios.post(endpoint, payload);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success(
          type === "group"
            ? "Groups moved successfully"
            : "Individuals moved successfully"
        );
        // Invalidate the query to refresh the list
        if (groupId) {
          invalidateAccreditationGroups(queryClient, parseInt(groupId));
          queryClient.invalidateQueries(["individuals", groupId, "", 1]);
        }
        closeAction();
      },
      onError: () => {
        toast.error(
          type === "group"
            ? "Failed to move groups"
            : "Failed to move individuals"
        );
      },
    }
  );
  console.log(queryClient);

  return (
    <div className="p-4 w-72">
      {groupOptions && !isGroupsLoading ? (
        <>
          <div className="mb-2">
            <p className="text-xs dark:text-zinc-400 font-medium">
              {type === "group" ? "Move group to" : "Move individual to"}
            </p>
          </div>
          <Formik
            initialValues={{ group: "" }}
            onSubmit={(values) => {
              console.log("Submitting move action with values:", values);
              moveMutation.mutate({
                ids: Array.from(selectedIds),
                targetGroup: parseInt(values.group.id),
              });
            }}
          >
            {({ values, handleSubmit }) => (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <Dropdown name="group" options={groupOptions} />

                <div className="mt-3 flex justify-between">
                  <button
                    type="button"
                    onClick={() => closeAction()}
                    className={clsx(
                      "mr-2 flex items-center border rounded-md px-2 py-0.5 dark:hover:bg-zinc-800 flex cursor-pointer group transition duration-200 ease-in-out",
                      "dark:border-white/10 dark:bg-zinc-700/10 dark:text-zinc-300 text-zinc-500",
                      "border-transparent hover:border-zinc-200 dark:hover:dark:border-white/5 hover:text-zinc-600 dark:hover:text-zinc-300 hover:border-white/10 dark:hover:bg-dark-primary text-zinc-500 hover:text-zinc-300"
                    )}
                  >
                    <span className="text-sm">Cancel</span>
                  </button>
                  <button
                    type="submit"
                    disabled={moveMutation.isLoading}
                    className={clsx(
                      "mr-0.5 flex items-center border rounded-md px-2 py-0.5 dark:hover:bg-zinc-800 flex cursor-pointer group transition duration-200 ease-in-out",
                      "dark:border-white/10 dark:bg-zinc-700/10 dark:text-zinc-300 text-zinc-500",
                      "border-transparent hover:border-zinc-200 dark:hover:dark:border-white/5 hover:text-zinc-600 dark:hover:text-zinc-300 hover:border-white/10 dark:hover:bg-dark-primary text-zinc-500 hover:text-zinc-300"
                    )}
                  >
                    {moveMutation.isLoading ? (
                      <ButtonSpinner />
                    ) : (
                      <>
                        <span className="text-sm">Move {type}</span>
                        <CornerUpRight className="ml-2 size-4 text-zinc-500 group-hover:text-zinc-300 transition" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </>
      ) : (
        <div className="flex items-center justify-center">
          <ButtonSpinner colour={darkMode ? "white" : "black"} />
        </div>
      )}
    </div>
  );
};
